import React, { useState, useEffect } from 'react';
import { pedidosService, Pedido, Seguimiento } from '../src/services/pedidosService';
import { useTheme } from '../src/contexts/ThemeContext';

interface OrderTrackingProps {
  numeroPedido?: string;
  pedidoId?: number;
  onBack: () => void;
}

const estatusConfig: Record<string, { title: string; icon: string; order: number }> = {
  Pendiente: { title: 'Pedido recibido', icon: 'check', order: 1 },
  Preparando: { title: 'Preparando', icon: 'soup_kitchen', order: 2 },
  EnCamino: { title: 'En camino', icon: 'two_wheeler', order: 3 },
  Entregado: { title: 'Entregado', icon: 'home', order: 4 },
  Cancelado: { title: 'Cancelado', icon: 'cancel', order: 5 },
};

const OrderTracking: React.FC<OrderTrackingProps> = ({ numeroPedido, pedidoId, onBack }) => {
  const { theme } = useTheme();
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPedido = async () => {
      try {
        setLoading(true);
        setError(null);
        let data: Pedido;
        if (numeroPedido) {
          data = await pedidosService.getByNumeroPedido(numeroPedido);
        } else if (pedidoId) {
          data = await pedidosService.getById(pedidoId);
        } else {
          throw new Error('Se requiere número de pedido o ID');
        }
        setPedido(data);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error al cargar pedido';
        const isNetworkError = msg === 'Load failed' || msg === 'Failed to fetch' || msg === 'NetworkError when attempting to access resource.';
        setError(isNetworkError ? 'No se pudo conectar con el servidor' : msg);
      } finally {
        setLoading(false);
      }
    };
    fetchPedido();
  }, [numeroPedido, pedidoId]);

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  const formatEstimada = (dateStr: string | null) => {
    if (!dateStr) return 'Por confirmar';
    const d = new Date(dateStr);
    const h = d.getHours();
    const m = d.getMinutes();
    return `${h}:${m.toString().padStart(2, '0')} - ${h}:${(m + 15).toString().padStart(2, '0')}`;
  };

  const getProgress = (estatus: string) => {
    const order = estatusConfig[estatus]?.order || 1;
    if (estatus === 'Cancelado') return 100;
    return Math.min((order / 4) * 100, 100);
  };

  const buildSteps = (seguimientos: Seguimiento[], currentEstatus: string) => {
    const allSteps = ['Pendiente', 'Preparando', 'EnCamino', 'Entregado'];
    const currentOrder = estatusConfig[currentEstatus]?.order || 1;

    return allSteps.map((estatus, i) => {
      const seg = seguimientos.find(s => s.estatus === estatus);
      const config = estatusConfig[estatus];
      const stepOrder = config.order;
      let status: 'done' | 'active' | 'pending' = 'pending';
      if (stepOrder < currentOrder) status = 'done';
      else if (stepOrder === currentOrder) status = 'active';

      return {
        title: config.title,
        desc: seg ? formatTime(seg.fechaCreacion) : (status === 'pending' ? 'Pendiente' : ''),
        icon: config.icon,
        status,
        index: i,
      };
    });
  };

  if (loading) {
    return (
      <div className="max-w-[1200px] mx-auto p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !pedido) {
    return (
      <div className="max-w-[1200px] mx-auto p-8 md:p-8 lg:p-12">
        <div className="flex flex-col items-center justify-center py-12">
          <span className="material-symbols-outlined text-gray-400 dark:text-gray-600 text-6xl mb-4 animate-fade-in">
            error_outline
          </span>
          <h2 className="text-2xl font-bold mb-2 animate-fade-in dark:text-white" style={{ animationDelay: '100ms' }}>
            {error || 'No se encontró el pedido'}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 animate-fade-in" style={{ animationDelay: '200ms' }}>
            No se pudo cargar la información del pedido
          </p>
          <button
            onClick={onBack}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-all hover:scale-105 active:scale-95 animate-bounce-in"
            style={{ animationDelay: '300ms' }}
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  const steps = buildSteps(pedido.seguimientos, pedido.estatus);
  const isCancelled = pedido.estatus === 'Cancelado';

  return (
    <div className="max-w-[1230px] mx-auto p-2 md:p-2 lg:p-2">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 mt-20">
        <div>
          <div className="flex items-center gap-2 text-sm text-[#896f61] dark:text-[#b59b8d] mb-1 animate-fade-in">
            <button onClick={onBack} className="hover:text-primary transition-colors">Pedidos</button>
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
            <span>{pedido.numeroPedido}</span>
          </div>
          <h1 className="text-[#181311] dark:text-white tracking-tight text-3xl md:text-4xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '100ms' }}>
            Seguimiento del Pedido {pedido.numeroPedido}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 h-full">
        {/* Sidebar Info */}
        <div className="lg:col-span-4 flex flex-col gap-6">
          <div className="bg-gray-100 dark:bg-[#1a120d] rounded-xl p-6 shadow-xl border border-gray-100 dark:border-white/10 animate-slide-up" style={{ animationDelay: '300ms' }}>
            <div className="flex items-center gap-4 mb-4">
              <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <span className="material-symbols-outlined">schedule</span>
              </div>
              <div>
                <p className="text-[#896f61] dark:text-[#b59b8d] text-sm font-medium">Llegada estimada</p>
                <p className="text-[#181311] dark:text-white text-2xl font-bold">
                  {formatEstimada(pedido.llegadaEstimada)}
                </p>
              </div>
            </div>
            <div className="w-full bg-gray-100 dark:bg-white/10 rounded-full h-2 mb-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${isCancelled ? 'bg-red-500' : 'bg-primary'}`}
                style={{ width: `${getProgress(pedido.estatus)}%` }}
              />
            </div>
            <p className="text-xs text-[#896f61] dark:text-[#b59b8d] text-right">
              {isCancelled ? 'Cancelado' : pedido.estatus === 'Entregado' ? 'Entregado' : 'A tiempo'}
            </p>
          </div>

          <div className="bg-gray-100 dark:bg-[#1a120d] rounded-xl p-6 shadow-xl border border-gray-100 dark:border-white/10 animate-slide-up" style={{ animationDelay: '400ms' }}>
            <h3 className="text-[#181311] dark:text-white text-lg font-bold mb-6">Estado del pedido</h3>
            <div className="flex flex-col gap-0">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-4 animate-fade-in" style={{ animationDelay: `${500 + (i * 100)}ms` }}>
                  <div className="flex flex-col items-center">
                    <div className={`size-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      step.status === 'done' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' :
                      step.status === 'active' ? 'bg-primary text-white shadow-lg animate-pulse' :
                      'bg-gray-100 dark:bg-white/10 text-gray-400 dark:text-gray-500'
                    }`}>
                      <span className="material-symbols-outlined text-[18px]">{step.icon}</span>
                    </div>
                    {i < steps.length - 1 && (
                      <div className={`w-[2px] h-10 ${step.status === 'done' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-white/10'}`} />
                    )}
                  </div>
                  <div className="pt-1">
                    <p className={`text-base font-bold ${step.status === 'active' ? 'text-primary' : 'text-gray-900 dark:text-white'}`}>
                      {step.title}
                    </p>
                    <p className="text-[#896f61] dark:text-[#b59b8d] text-sm">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Info del negocio y repartidor */}
          <div className="bg-gray-100 dark:bg-[#1a120d] rounded-xl p-6 shadow-xl border border-gray-100 dark:border-white/10 animate-slide-up" style={{ animationDelay: '500ms' }}>
            <h3 className="text-[#181311] dark:text-white text-lg font-bold mb-4">Detalles</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[#896f61] dark:text-[#b59b8d]">Negocio</span>
                <span className="text-gray-900 dark:text-white font-medium">{pedido.nombreNegocio}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#896f61] dark:text-[#b59b8d]">Repartidor</span>
                <span className="text-gray-900 dark:text-white font-medium">{pedido.nombreEmpleado || 'Por asignar'}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-white/10 pt-3 mt-1 space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#896f61] dark:text-[#b59b8d]">Subtotal</span>
                  <span className="text-gray-900 dark:text-white font-medium">${pedido.subtotal?.toFixed(2) ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#896f61] dark:text-[#b59b8d]">Tarifa de servicio</span>
                  <span className="text-gray-900 dark:text-white font-medium">${pedido.tarifaServicio?.toFixed(2) ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#896f61] dark:text-[#b59b8d]">Costo de envío</span>
                  <span className="text-gray-900 dark:text-white font-medium">${pedido.importeEnvio?.toFixed(2) ?? '—'}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-white/10">
                  <span className="text-gray-900 dark:text-white font-black">Total</span>
                  <span className="text-primary font-bold text-base">${pedido.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="lg:col-span-8 h-[600px] rounded-2xl overflow-hidden relative shadow-md border border-gray-100 dark:border-white/10 animate-scale-in" style={{ animationDelay: '500ms' }}>
          {pedido.latitud && pedido.longitud ? (
            <iframe
              className="w-full h-full"
              style={{ 
                border: 0,
                filter: theme === 'dark' ? 'invert(90%) hue-rotate(180deg) brightness(1.1) contrast(1.1)' : 'none'
              }}
              loading="lazy"
              src={`https://www.google.com/maps?q=${pedido.latitud},${pedido.longitud}&z=15&output=embed`}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
              <div className="text-center text-gray-500 dark:text-gray-400">
                <span className="material-symbols-outlined text-6xl mb-2">map</span>
                <p>Ubicación no disponible</p>
              </div>
            </div>
          )}
          {/* Address Overlay */}
          <div className="absolute top-4 left-4 bg-white/95 dark:bg-[#1a120d]/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-gray-100 dark:border-white/10 max-w-80">
            <p className="text-[10px] uppercase tracking-wider text-[#896f61] dark:text-[#b59b8d] font-bold mb-1">Entregar en</p>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-primary mt-0.5">location_on</span>
              <div>
                <p className="text-[#181311] dark:text-white font-bold leading-tight">{pedido.direccionEntrega}</p>
                <p className="text-xs text-[#896f61] dark:text-[#b59b8d] mt-0.5">{pedido.nombreCliente} • {pedido.telefonoCliente}</p>
              </div>
            </div>
          </div>
          {/* Status badge */}
          {pedido.estatus === 'EnCamino' && pedido.nombreEmpleado && (
            <div className="absolute top-4 right-4 bg-primary text-white px-4 py-2 rounded-xl shadow-lg flex items-center gap-2 animate-pulse">
              <span className="material-symbols-outlined">two_wheeler</span>
              <span className="font-bold">{pedido.nombreEmpleado} en camino</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
